import { useEmpresasAuditaveis } from "../../lib/auditoria/hooks";
import type { EmpresaAuditavel } from "../../lib/auditoria/tipos";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Seletor de empresa da trilha de auditoria (FE-5 CP-C — LEITURA).
 *
 * Espelha o `conciliacao/SeletorEmpresaConciliacao`, mas consome
 * `useEmpresasAuditaveis()` (GET /api/auditoria/empresas) — a lista JÁ vem
 * filtrada pelo escopo `ver_auditoria` do usuário no backend; a UI nunca filtra
 * empresa do lado do cliente.
 *
 * Rótulo: `fantasia ?? razao_social`. DIFERENÇA da conciliação: a auditoria é
 * FORENSE — a lista PODE incluir empresas INATIVAS (`ativa === false`); quando
 * for o caso, anexamos " (inativa)" ao rótulo para o auditor saber.
 *
 * ESCOPO VAZIO (`[]`): estado claro em vez de um `<select>` vazio confuso.
 */

interface SeletorEmpresaAuditavelProps {
  /** Id da empresa atualmente selecionada, ou `null` enquanto nada foi escolhido. */
  selecionada: string | null;
  /** Notifica a página da empresa escolhida. */
  aoSelecionar: (empresaId: string) => void;
}

/** Rótulo de exibição: `fantasia ?? razao_social`, com sufixo se inativa. */
function rotuloEmpresa(empresa: EmpresaAuditavel): string {
  const base = empresa.fantasia ?? empresa.razao_social;
  return empresa.ativa ? base : `${base} (inativa)`;
}

export function SeletorEmpresaAuditavel({
  selecionada,
  aoSelecionar,
}: SeletorEmpresaAuditavelProps): JSX.Element {
  const consulta = useEmpresasAuditaveis();

  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">
        Carregando empresas…
      </p>
    );
  }

  if (consulta.isError) {
    const mensagem =
      consulta.error instanceof ErroHttp
        ? consulta.error.message
        : "Não foi possível carregar as empresas.";
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagem}
      </p>
    );
  }

  const empresas = consulta.data;

  // ESCOPO VAZIO: nada a auditar — estado claro, não um seletor vazio.
  if (empresas.length === 0) {
    return (
      <p className="font-corpo text-base text-off-black">
        Você não tem empresas para auditar.
      </p>
    );
  }

  function aoMudar(evento: React.ChangeEvent<HTMLSelectElement>): void {
    const valor = evento.target.value;
    if (valor === "") {
      return;
    }
    aoSelecionar(valor);
  }

  return (
    <label className="flex flex-col gap-1 font-corpo text-base text-off-black">
      <span className="font-subtitulo">Empresa</span>
      <select
        value={selecionada ?? ""}
        onChange={aoMudar}
        className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
      >
        <option value="" disabled>
          Selecione…
        </option>
        {empresas.map((empresa) => (
          <option key={empresa.id} value={empresa.id}>
            {rotuloEmpresa(empresa)}
          </option>
        ))}
      </select>
    </label>
  );
}
